import { CircularProgress, TextareaAutosize, TextField } from "@mui/material";
import React, { useState } from "react";
import { FaEye, FaEyeSlash, FaSearch } from "react-icons/fa";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { Col } from "reactstrap";

export type InputComponentProps = {
    label: string;
    autoSelectOnFocus?: boolean;
    clearZeroOnFocus?: boolean;
    required?: boolean;
    'data-testid'?: string; // para usar no Playwright
} & (
        | {
            readOnly: true;
            onChange?: (text: any) => void;
        }
        | {
            readOnly?: false;
            onChange: (text: any) => void;
            onEnter?: (e: React.KeyboardEvent<HTMLInputElement>) => void;
            onFocusOut?: () => void;
        }
    ) &
    (
        | {
            type: "number";
            value: number | undefined | null | string; //string porque o campo phoneNumber é string mas apenas aceita input number
        }
        | {
            type: "text";
            value: string | undefined | null;
        }
        | {
            type: "date";
            value?: Date | string | undefined | null;
        }
        | {
            type: "time";
            value: string | undefined | null;
        }
        | {
            type: "textArea";
            value: string | undefined | null;
            rows?: number | undefined;
        }
        | {
            type: "password";
            value: string | undefined;
        }
    ) & {
        style?: React.CSSProperties | undefined;
    } & {
        class?: string | undefined;
    } & {
        min?: number | undefined;
        max?: number | undefined;
    } & {
        xs?: number;
        sm?: number;
        md?: number;
        lg: number;
        xl?: number;
        placeholder?: string;
    } & {
        showIcon?: boolean;
        functionIcon?: () => void;
        isFunctionLoading?: boolean;
    } & {
        showLoadingSkeleton?: boolean;
    };

export default function InputComponent(props: InputComponentProps) {
    const handleFocus = (e: React.FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        if (props.autoSelectOnFocus && e.target && typeof e.target.select === 'function') {
            e.target.select();
        }

        if (props.clearZeroOnFocus && props.type === "number" && (props.value === 0 || props.value === "0")) {
            if (props.onChange) {
                props.onChange("");
            }
        }
    };

    const onInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        if (props.onChange) {
            props.onChange(e.target.value);
        }
    };

    const onTextAreaChange = (e: any) => {
        if (typeof props.readOnly != "undefined" && props.readOnly !== true) {
            props.onChange(e.target.value);
        }
    };

    const onKeyPress = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Enter") {
            if (props.readOnly === false && typeof props.onEnter !== "undefined") {
                props.onEnter(e);
            }
        }
    };

    const onKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (
            e.key === "Tab" &&
            props.readOnly === false &&
            typeof props.onFocusOut !== "undefined"
        ) {
            props.onFocusOut();
        }
    };

    const [viewPassword, setViewPassword] = useState<boolean>(false)

    return (
        <Col
            xl={props.xl}
            lg={props.lg}
            md={props.md}
            sm={props.sm}
            xs={props.xs}
            className={`inputContainer ${props.class}`}
        >

            <span className="label">
                {props.label}
                {props.required && <span style={{ marginRight: 2, fontSize: '14px' }}>*</span>}
                :
            </span>

            {typeof props.showLoadingSkeleton !== "undefined" &&
                props.showLoadingSkeleton === true ? (
                <>
                    <Skeleton height={"2rem"} />
                </>
            ) : (
                <>
                    {props.type !== "date" && props.type !== "time" && props.type !== "password" && props.showIcon === true && (
                        <div className="input-icons">
                            <input
                                data-testid={props['data-testid']} //inserir dentro do compo de input para Playwright
                                onKeyPress={onKeyPress}
                                onKeyDown={onKeyDown}
                                className="input"
                                type={props.type}
                                readOnly={props.readOnly}
                                value={props.value !== null && props.value !== undefined ? props.value : ''}
                                onChange={onInputChange}
                                style={props.style}
                                min={props.min}
                                max={props.max}
                                ></input>
                            {props.isFunctionLoading ? (
                                <a className="icon">
                                    <CircularProgress size={"1.5em"} />
                                </a>
                            ) : (
                                <a
                                    className="icon"
                                    style={{ cursor: "pointer" }}
                                    onClick={props.functionIcon}
                                >
                                    <FaSearch />
                                </a>
                            )}
                        </div>
                    )}

                    {props.type !== "date" &&
                        props.type !== "time" &&
                        props.type !== "textArea" &&
                        props.type !== "password" &&
                        props.showIcon !== true && (
                            <input
                                data-testid={props['data-testid']} 
                                onKeyPress={onKeyPress}
                                onKeyDown={onKeyDown}
                                onFocus={handleFocus}
                                className="input"
                                type={props.type}
                                readOnly={props.readOnly}
                                value={props.value !== null && props.value !== undefined ? props.value : ''}
                                onChange={onInputChange}
                                style={props.style}
                                min={props.min}
                                max={props.max}
                                placeholder={props.placeholder}
                            ></input>
                        )}

                    {props.type === "password" && (
                        <div className="input-icons">
                            <input
                                data-testid={props['data-testid']} // Aplicar data-testid
                                onKeyPress={onKeyPress}
                                onKeyDown={onKeyDown}
                                className="input"
                                type={viewPassword == false ? props.type : "text"}
                                readOnly={props.readOnly}
                                value={props.value}
                                onChange={onInputChange}
                                style={props.style}
                                min={props.min}
                                max={props.max}
                                ></input>

                            <a className="icon"
                                style={{ cursor: "pointer" }}>
                                {viewPassword === false ?
                                    <FaEyeSlash size={"1.5em"} onClick={() => setViewPassword(true)} />
                                    :
                                    <FaEye size={"1.5em"} onClick={() => setViewPassword(false)} />}
                            </a>
                        </div>
                    )}

                    {props.type === "textArea" && (
                        <TextareaAutosize
                            data-testid={props['data-testid']} 
                            className="input"
                            minRows={props.rows || 4}
                            maxRows={15}
                            readOnly={props.readOnly}
                            value={props.value !== null && props.value !== undefined ? props.value : ''}
                            onChange={onTextAreaChange}
                            style={{ width: "100%", ...props.style }}
                            placeholder={props.placeholder}
                        />
                    )}

                    {props.type === "date" && (
                        <TextField
                            data-testid={props['data-testid']} 
                            value={props.value}
                                className={`inputMaterialUi ${props.readOnly ? "disabledFieldsetMaterialDate" : ""
                                    }`}
                            type="date"
                            variant={"outlined"}
                            onKeyPress={onKeyPress}
                            onChange={onInputChange}
                            style={props.style}
                            inputProps={{
                                'data-testid': props['data-testid'] ? `${props['data-testid']}-input` : undefined
                            }}
                        />
                    )}

                    {props.type === "time" && (
                        <TextField
                            data-testid={props['data-testid']} 
                            value={props.value}
                                className={`inputMaterialUi ${props.readOnly ? "disabledFieldsetMaterialDate" : ""
                                    }`}
                            type="time"
                            variant={"outlined"}
                            onKeyPress={onKeyPress}
                            onChange={onInputChange}
                            style={props.style}
                            inputProps={{
                                'data-testid': props['data-testid'] ? `${props['data-testid']}-input` : undefined
                            }}
                        />
                    )}
                </>
            )}
        </Col>
    );
}